import Highcharts from "highcharts";
import Accessibility from "highcharts/modules/accessibility";
import DataGrouping from "highcharts/modules/datagrouping";

Accessibility(Highcharts);
DataGrouping(Highcharts);

Highcharts.setOptions({
  lang: {
    months: [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ],
    shortMonths: [
      "Ene", "Feb", "Mar", "Abr", "May", "Jun",
      "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"
    ],
    weekdays: [
      "Domingo", "Lunes", "Martes", "Miércoles",
      "Jueves", "Viernes", "Sábado"
    ],
    decimalPoint: ",",
    thousandsSep: ".",
    loading: "Cargando...",
    resetZoom: "Reiniciar zoom",
    resetZoomTitle: "Reiniciar nivel de zoom 1:1",
  },
  time: {
    timezone: "Europe/Madrid",
    useUTC: false
  }
});

const base = {
  title: { text: undefined },
  credits: { enabled: false },
  legend: { enabled: false },
  tooltip: { shared: true, borderRadius: 8, padding: 10 },
  chart: {
    backgroundColor: "transparent",
    zoomType: "x",
    spacingTop: 12,
    spacingBottom: 6,
  },
  exporting: { enabled: false },
};

export function themeGrid() {
  const x = {
    lineColor: "#e5e7eb",
    tickColor: "#e5e7eb",
    gridLineColor: "#eef0f4",
    labels: { style: { color: "#6b7280" } },
  };
  const y = {
    gridLineColor: "#eef0f4",
    labels: { style: { color: "#6b7280" } },
    title: { text: undefined },
  };
  return { xAxis: x, yAxis: y };
}

function getGroupingForScope(scope) {
  switch (scope) {
    case "quarter":
      return [["minute", [15]]];
    case "hour":
      return [["hour", [1]]];
    case "day":
      return [["day", [1]]];
    case "month":
      return [["month", [1]]];
    case "year":
      return [["year", [1]]];
    default:
      return [["hour", [1]]];
  }
}

/* ===========================================================
   === ÁREA TEMPORAL DE INGRESOS =============================
   =========================================================== */
export function makeAreaTimeseries(p) {
  const {
    data = [],
    startDate,
    scope = "hour",
    color = "#ff7a59",
    yTitle = "Ingresos (€)",
  } = p;

  const start = new Date(startDate);
  const seriesData = data.map((v, i) => [
    start.getTime() + i * 15 * 60 * 1000,
    v,
  ]);

  const grid = themeGrid();

  return {
    ...base,
    ...grid,
    chart: { ...base.chart, type: "areaspline" },
    xAxis: {
      ...grid.xAxis,
      type: "datetime",
      title: { text: "Periodo" },
    },
    yAxis: { ...grid.yAxis, title: { text: yTitle } },
    tooltip: {
      xDateFormat: "%d %b %Y, %H:%M",
      pointFormat: "<b>{point.y:.2f} €</b>",
      useHTML: true,
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: true,
          forced: true,
          approximation: "sum",
          units: getGroupingForScope(scope),
        },
        marker: { enabled: false },
        animation: false,
      },
      areaspline: {
        fillOpacity: 0.18,
        lineWidth: 2,
        color,
      },
    },
    series: [{ name: "Ingresos", data: seriesData }],
  };
}

/* ===========================================================
   === ÁREA TEMPORAL DE PEDIDOS ==============================
   =========================================================== */
export function makeOrdersTimeseries(p) {
  const {
    data = [],
    startDate,
    scope = "hour",
    color = "#b9a9ff",
    yTitle = "Pedidos",
  } = p;

  const start = new Date(startDate);
  const seriesData = data.map((v, i) => [
    start.getTime() + i * 15 * 60 * 1000,
    v,
  ]);

  const grid = themeGrid();

  return {
    ...base,
    ...grid,
    chart: { ...base.chart, type: "areaspline" },
    xAxis: {
      ...grid.xAxis,
      type: "datetime",
      title: { text: "Periodo" },
    },
    yAxis: { ...grid.yAxis, title: { text: yTitle } },
    tooltip: {
      xDateFormat: "%d %b %Y, %H:%M",
      pointFormat: "<b>{point.y} pedidos</b>",
      useHTML: true,
    },
    plotOptions: {
      series: {
        dataGrouping: {
          enabled: true,
          forced: true,
          approximation: "sum",
          units: getGroupingForScope(scope),
        },
        marker: { enabled: false },
        animation: false,
      },
      areaspline: {
        fillOpacity: 0.18,
        lineWidth: 2,
        color,
      },
    },
    series: [{ name: "Pedidos", data: seriesData }],
  };
}

/* ===========================================================
   === DONUT: VENTAS POR CANAL ===============================
   =========================================================== */
export function makeDonut(p) {
  const {
    series,
    colors = ["#ff7a59", "#3b82f6", "#22c55e"],
    innerSize = "68%",
  } = p;
  return {
    ...base,
    chart: { ...base.chart, type: "pie" },
    tooltip: {
      useHTML: true,
      formatter() {
        return `
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${this.point.color};margin-right:6px;"></span>
          <b>${this.point.name}</b><br/>
          Ventas: <b>${this.y.toFixed(2)} €</b>
        `;
      },
    },
    plotOptions: {
      pie: {
        innerSize,
        dataLabels: { enabled: false },
        colors,
      },
    },
    series,
  };
}

/* ===========================================================
   === BARRAS: TICKETS POR CANAL =============================
   =========================================================== */
export function makeBars(p) {
  const { categories, series, color = "#6d6af7", stacking } = p;
  const grid = themeGrid();
  return {
    ...base,
    ...grid,
    chart: { ...base.chart, type: "column" },
    xAxis: { ...grid.xAxis, categories },
    plotOptions: {
      column: {
        borderRadius: 6,
        pointPadding: 0.1,
        groupPadding: 0.08,
        color,
      },
      series: { stacking: stacking ?? undefined, animation: false },
    },
    series,
  };
}